import { useLayoutEffect, useRef, useState } from 'react';

import { MenuItem, OutlinedInput, Tooltip } from '@mui/material';

import { useField } from 'formik';

import { FormControl, Label } from 'components/UIComponents';

import { ISelectOption } from 'interfaces/ISelectOption';

import { InputFieldContainer, StyledSelect, TruncatedOptionText } from './styledComponents';

interface SelectProps {
  label: string;
  name: string;
  options: ISelectOption[];
  onChange?: any;
  styles?: {
    wrapper?: object;
    label?: object;
  };
  placeholder?: string;
  required?: boolean;
  direction?: 'column' | 'row';
  emptyOptionLabel?: string;
  allowEmpty?: boolean;
  disabled?: boolean;
  truncateOptions?: boolean;
}

const TRUNCATED_MENU_MAX_WIDTH = 480;
const TOOLTIP_MAX_WIDTH = 360;

interface TruncatedOptionProps {
  text: string;
}

const TruncatedOption = ({ text }: TruncatedOptionProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useLayoutEffect(() => {
    if (ref.current) {
      setIsOverflowed(ref.current.scrollWidth > ref.current.clientWidth);
    }
  }, [text]);

  return (
    <Tooltip
      title={text}
      placement="right"
      disableHoverListener={!isOverflowed}
      disableFocusListener={!isOverflowed}
      disableTouchListener={!isOverflowed}
      enterDelay={150}
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: TOOLTIP_MAX_WIDTH,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            fontSize: 13,
            lineHeight: 1.45,
            padding: '8px 10px',
          },
        },
      }}
    >
      <TruncatedOptionText ref={ref}>{text}</TruncatedOptionText>
    </Tooltip>
  );
};

const Select = ({
  onChange,
  label,
  name = '',
  options = [],
  styles = {},
  placeholder = '',
  required = false,
  emptyOptionLabel = 'None',
  direction,
  allowEmpty = true,
  disabled = false,
  truncateOptions = false,
}: SelectProps) => {
  const [field, meta] = useField(name);

  const menuPaperSx = truncateOptions
    ? { maxHeight: 200, maxWidth: TRUNCATED_MENU_MAX_WIDTH }
    : { maxHeight: 200 };

  return (
    <FormControl direction={direction} styles={styles.wrapper}>
      <Label required={required} style={styles.label} disabled={disabled}>
        {label}
      </Label>
      <InputFieldContainer>
        <StyledSelect
          {...field}
          input={<OutlinedInput inputProps={{ 'aria-label': label }} />}
          variant="standard"
          error={meta.touched && !!meta.error}
          onChange={(e) => {
            field.onChange(e);
            onChange?.(e);
          }}
          placeholder={placeholder}
          MenuProps={{ PaperProps: { sx: menuPaperSx } }}
          disabled={disabled}
        >
          {!required && allowEmpty && (
            <MenuItem value="" key="none">
              <em>{emptyOptionLabel}</em>
            </MenuItem>
          )}
          {options.map(({ key, value }) => (
            <MenuItem
              key={String(value)}
              value={value as any}
              sx={truncateOptions ? { maxWidth: '100%', overflow: 'hidden' } : undefined}
            >
              {truncateOptions ? <TruncatedOption text={String(key)} /> : <span>{key}</span>}
            </MenuItem>
          ))}
        </StyledSelect>
        <Label error>{meta.touched && meta.error}</Label>
      </InputFieldContainer>
    </FormControl>
  );
};

export default Select;
